import {
  ActionViewModel,
  ActionViewModelImplementation,
  durationInSecondsOrZero,
  Effector,
  secondsToDuration,
  uuidOrNewUuid,
} from "./ActionViewModel";
import { AdvancedAction, QuickAction } from "protos/atk/activities";
import { carbonOnlyAllEffectorsWithType, StartDelayInput } from "./utils";
import { Field } from "formik";
import { FormControl, FormLabel } from "@mui/material";
import { FormikNumberInput } from "common/components/FormikNumberInput";
import { RigEffectiveConfig } from "protos/atk/effective_config";
import { TextField } from "formik-mui";
import { TFunction } from "i18next";
import { useRandomId } from "portal/utils/hooks/useRandomId";
import { useTranslation } from "react-i18next";
import ActionIcon from "@mui/icons-material/Settings";
import React from "react";

class AdvancedQuickViewModel extends ActionViewModel<AdvancedQuickViewModel> {
  constructor(
    effector: Effector,
    startInStageIndex: number,
    startDelaySec: number,
    public actionId: string,
    public actionData: AdvancedAction
  ) {
    super(effector, false, startInStageIndex, startDelaySec);
  }

  override toQuickAction(_finalActionThisEffector: boolean): QuickAction {
    return {
      actionId: uuidOrNewUuid(this.actionId),
      startDelay: secondsToDuration(this.startDelaySec),
      carryOverIntoWorkTask: false,
      advanced: this.actionData,
    };
  }

  override actionCardTitle(
    t: TFunction,
    _rigEffectiveConfig: RigEffectiveConfig,
    stageIndex: number,
    _finalActionThisEffector: boolean
  ): string | undefined {
    if (stageIndex === this.startInStageIndex) {
      return t(
        "views.autotractor.autonomousActivities.action.cardTitle.advanced",
        { actionName: this.actionData.actionName }
      );
    }
  }

  override effectorBlockedByActionMessage(t: TFunction): string {
    return t(
      "views.autotractor.autonomousActivities.action.blockingMessage.advanced",
      { actionName: this.actionData.actionName }
    );
  }

  override components = advancedQuick.components;
}
export const advancedQuick: ActionViewModelImplementation<AdvancedQuickViewModel> =
  {
    actionButtonLabelI18nKey:
      "views.autotractor.autonomousActivities.action.buttonLabel.advanced",
    actionMenuSection: "quick-action",
    isNewActionAvailableForEffector: carbonOnlyAllEffectorsWithType("advanced"),
    newAction: (_effector, startInStageIndex) => {
      return new AdvancedQuickViewModel(
        { type: "advanced" },
        startInStageIndex,
        0,
        "",
        {
          actionName: "",
          intParam: 0,
          floatParam: 0,
        }
      );
    },
    fromQuickAction: (quickAction, startInStageIndex) => {
      if (quickAction.advanced && !quickAction.carryOverIntoWorkTask) {
        return new AdvancedQuickViewModel(
          { type: "advanced" },
          startInStageIndex,
          durationInSecondsOrZero(quickAction.startDelay),
          quickAction.actionId,
          quickAction.advanced
        );
      }
    },
    components: {
      Icon: ActionIcon,
      EditForm: ({ basePath }) => {
        const { t } = useTranslation();
        const actionNameLabelId = useRandomId();

        return (
          <div className="grid grid-cols-2 gap-2">
            <FormControl fullWidth className="col-span-full">
              <FormLabel id={actionNameLabelId}>
                {t(
                  "views.autotractor.autonomousActivities.action.parameterName.actionName"
                )}
              </FormLabel>
              <Field
                component={TextField}
                name={basePath.actionData.actionName.$path}
                aria-labelledby={actionNameLabelId}
              />
            </FormControl>
            <FormikNumberInput
              label={t(
                "views.autotractor.autonomousActivities.action.parameterName.intParam"
              )}
              name={basePath.actionData.intParam.$path}
            />
            <FormikNumberInput
              label={t(
                "views.autotractor.autonomousActivities.action.parameterName.floatParam"
              )}
              name={basePath.actionData.floatParam.$path}
              numberInputProps={{
                step: "any",
              }}
            />
            <StartDelayInput path={basePath.startDelaySec} />
          </div>
        );
      },
    },
  };

class AdvancedHoldingViewModel extends ActionViewModel<AdvancedHoldingViewModel> {
  constructor(
    effector: Effector,
    startInStageIndex: number,
    startDelaySec: number,
    public actionId: string | undefined,
    public actionData: AdvancedAction
  ) {
    super(effector, true, startInStageIndex, startDelaySec);
  }

  override toQuickAction(_finalActionThisEffector: boolean): QuickAction {
    return {
      actionId: uuidOrNewUuid(this.actionId),
      advanced: this.actionData,
      startDelay: secondsToDuration(this.startDelaySec),
      carryOverIntoWorkTask: true,
    };
  }

  //eslint-disable-next-line sonarjs/no-identical-functions
  override actionCardTitle(
    t: TFunction,
    _rigEffectiveConfig: RigEffectiveConfig,
    stageIndex: number,
    _finalActionThisEffector: boolean
  ): string | undefined {
    if (stageIndex === this.startInStageIndex) {
      return t(
        "views.autotractor.autonomousActivities.action.cardTitle.advanced",
        { actionName: this.actionData.actionName }
      );
    }
  }

  //eslint-disable-next-line sonarjs/no-identical-functions
  override effectorBlockedByActionMessage(t: TFunction): string {
    return t(
      "views.autotractor.autonomousActivities.action.blockingMessage.advanced",
      { actionName: this.actionData.actionName }
    );
  }

  override components = advancedHolding.components;
}
export const advancedHolding: ActionViewModelImplementation<AdvancedHoldingViewModel> =
  {
    actionButtonLabelI18nKey:
      "views.autotractor.autonomousActivities.action.buttonLabel.advanced",
    actionMenuSection: "holding-action",
    isNewActionAvailableForEffector: carbonOnlyAllEffectorsWithType("advanced"),
    newAction: (_effector, startInStageIndex) => {
      return new AdvancedHoldingViewModel(
        { type: "advanced" },
        startInStageIndex,
        0,
        undefined,
        {
          actionName: "",
          intParam: 0,
          floatParam: 0,
        }
      );
    },
    fromQuickAction: (quickAction, startInStageIndex) => {
      if (quickAction.advanced && quickAction.carryOverIntoWorkTask) {
        return new AdvancedHoldingViewModel(
          { type: "advanced" },
          startInStageIndex,
          durationInSecondsOrZero(quickAction.startDelay),
          quickAction.actionId,
          quickAction.advanced
        );
      }
    },
    components: {
      Icon: ActionIcon,
      EditForm: ({ basePath }) => {
        const { t } = useTranslation();
        const actionNameLabelId = useRandomId();

        return (
          <div className="grid grid-cols-2 gap-2">
            <FormControl fullWidth className="col-span-full">
              <FormLabel id={actionNameLabelId}>
                {t(
                  "views.autotractor.autonomousActivities.action.parameterName.actionName"
                )}
              </FormLabel>
              <Field
                component={TextField}
                name={basePath.actionData.actionName.$path}
                aria-labelledby={actionNameLabelId}
              />
            </FormControl>
            <FormikNumberInput
              label={t(
                "views.autotractor.autonomousActivities.action.parameterName.intParam"
              )}
              name={basePath.actionData.intParam.$path}
            />
            <FormikNumberInput
              label={t(
                "views.autotractor.autonomousActivities.action.parameterName.floatParam"
              )}
              name={basePath.actionData.floatParam.$path}
              numberInputProps={{
                step: "any",
              }}
            />
            <StartDelayInput path={basePath.startDelaySec} />
          </div>
        );
      },
    },
  };
